import { useTranslation } from "react-i18next";
// src/pages/CustomerList.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/solid";

import EmptyState from "../components/EmptyState";
import {
  ListToolbar,
  type MenuItem,
  type SortOption,
} from "../components/ListToolbar";
import { apiGet } from "../api/client";
import { companyRoute } from "../utils/routeHelpers";
import { downloadCsv } from "../utils/downloadCsv";
import { Button } from "../components/Button";

// ✅ Apple Standard (your actual exports)
import { A } from "../styles/appleClasses";
import ListTableShell from "../components/lists/ListTableShell";

interface Customer {
  id: number;
  name: string;
  email: string;
  mobile: string;
}

const ROWS_PER_PAGE = 10;

type SortKey = "name" | "email" | "mobile";
type SortDir = "asc" | "desc";

/* =========================
   Refetch on focus/visibility (same behavior as SalesList)
   ========================= */
function useRefetchOnFocus(refetch: () => void) {
  useEffect(() => {
    const onFocus = () => refetch();
    const onVis = () => {
      if (document.visibilityState === "visible") refetch();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [refetch]);
}

function csvEscape(v: unknown) {
  const s = String(v ?? "");
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

type DisplayError = { local: "loadFailed" | "noCompany" } | { raw: string } | null;

export default function CustomerList() {
  const { t } = useTranslation();
  const errorText = (error: DisplayError): string | null => error == null ? null : "raw" in error ? error.raw : ({
    loadFailed: t("directory.customers.loadFailed"),
    noCompany: t("directory.customers.noCompany"),
  })[error.local];

  const navigate = useNavigate();
  const location = useLocation();
  const { company_id } = useParams<{ company_id: string }>();

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<DisplayError>(null);

  // Toolbar state
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // Sorting state
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  // --- shared style helpers (NO cx)
  const PAGE_W = "max-w-5xl";
  const iconBtnCls = [
    A.iconBtn,
    "inline-flex items-center justify-center",
    "hover:bg-gray-100",
    "focus:outline-none focus:ring-2 focus:ring-appleblueD",
  ].join(" ");
  const cardHoverCls = [
    "cursor-pointer",
    "hover:bg-gray-50",
    "transition",
  ].join(" ");
  const linkCls = "text-[13px] font-normal text-appleblue hover:text-appleblueD";

  const pagerText = (
    <span className={`${A.label} text-gray-500 select-none`}>
      Showing {0}–{0} of {0}
    </span>
  );
  const pagerBtnExtraCls = "px-2 active:scale-100";

  const fetchCustomers = useCallback(async () => {
    if (!company_id) return;

    setLoading(true);
    setError(null);

    try {
      const raw = await apiGet<any[]>(
        `/customers?company_id=${company_id}&_=${Date.now()}`,
      );

      const mapped: Customer[] = (raw || []).map((cust) => ({
        id: Number(cust.id),
        name: cust.name || cust.fullName || cust.customer_name || "—",
        email: cust.email || cust.contact?.email || "",
        mobile: cust.mobile || cust.contact?.mobile || "",
      }));

      setCustomers(mapped);
    } catch {
      setError({ local: "loadFailed" });
    } finally {
      setLoading(false);
    }
  }, [company_id]);

  useEffect(() => {
    if (!company_id) {
      setError({ local: "noCompany" });
      setLoading(false);
      return;
    }
    fetchCustomers();
  }, [company_id, fetchCustomers, location.key, location.search]);

  useRefetchOnFocus(fetchCustomers);

  useEffect(() => {
    setPage(1);
  }, [search, sortKey, sortDir, customers.length]);

  const clearFilters = () => {
    setSearch("");
    setSortKey("name");
    setSortDir("asc");
  };

  const filteredCustomers = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return customers;

    return customers.filter((c) => {
      const name = (c.name || "").toLowerCase();
      const email = (c.email || "").toLowerCase();
      const mobile = (c.mobile || "").toLowerCase();
      return name.includes(q) || email.includes(q) || mobile.includes(q);
    });
  }, [customers, search]);

  const sortedCustomers = useMemo(() => {
    const list = filteredCustomers.slice();
    const sign = sortDir === "asc" ? 1 : -1;

    const cmpStr = (a: string, b: string) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }) * sign;

    list.sort((a, b) => {
      if (sortKey === "email") {
        const c = cmpStr(a.email || "", b.email || "");
        if (c !== 0) return c;
        return cmpStr(a.name || "", b.name || "");
      }

      if (sortKey === "mobile") {
        const c = cmpStr(a.mobile || "", b.mobile || "");
        if (c !== 0) return c;
        return cmpStr(a.name || "", b.name || "");
      }

      const c = cmpStr(a.name || "", b.name || "");
      if (c !== 0) return c;
      return ((a.id || 0) - (b.id || 0)) * sign;
    });

    return list;
  }, [filteredCustomers, sortKey, sortDir]);

  const totalFiltered = sortedCustomers.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / ROWS_PER_PAGE));
  const pagedCustomers = sortedCustomers.slice(
    (page - 1) * ROWS_PER_PAGE,
    page * ROWS_PER_PAGE,
  );

  const fromRow = totalFiltered === 0 ? 0 : (page - 1) * ROWS_PER_PAGE + 1;
  const toRow = Math.min(page * ROWS_PER_PAGE, totalFiltered);

  // ✅ ListToolbar wiring (no date range)
  const sortValue = `${sortKey}_${sortDir}`;
  const sortOptions: SortOption[] = [
    { label: t("directory.common.nameAsc"), value: "name_asc" },
    { label: t("directory.common.nameDesc"), value: "name_desc" },
    { label: t("directory.common.emailAsc"), value: "email_asc" },
    { label: t("directory.common.emailDesc"), value: "email_desc" },
    { label: t("directory.common.mobileAsc"), value: "mobile_asc" },
    { label: t("directory.common.mobileDesc"), value: "mobile_desc" },
  ];

  const onToolbarSortChange = (v: string) => {
    const [k, d] = v.split("_") as [SortKey, SortDir];
    setSortKey(k);
    setSortDir(d);
  };

  const onHeaderSort = (k: SortKey) => {
    if (sortKey !== k) {
      setSortKey(k);
      setSortDir(
        k === "name" || k === "email" || k === "mobile" ? "asc" : "desc",
      );
      return;
    }
    setSortDir((d) => (d === "asc" ? "desc" : "asc"));
  };

  const sortArrow = (k: SortKey) =>
    sortKey === k ? (sortDir === "asc" ? "↑" : "↓") : null;

  const exportCsv = () => {
    const header = ["Name", "Email", "Mobile"];
    const lines = [
      header.join(","),
      ...sortedCustomers.map((c) =>
        [c.name, c.email, c.mobile].map(csvEscape).join(","),
      ),
    ];
    downloadCsv("customers", lines.join("\n"));
  };

  const newItems: MenuItem[] = company_id
    ? [
        {
          label: t("directory.customers.new"),
          onClick: () => navigate(companyRoute(company_id, "/customers/new")),
        },
      ]
    : [];

  const moreItems: MenuItem[] = [{ label: t("directory.common.exportCsv"), onClick: exportCsv }];

  if (loading)
    return (
      <div className={`text-center py-6 text-gray-600 ${A.body}`}>
        {t("directory.customers.loading")}
      </div>
    );
  if (error) return <EmptyState heading={t("directory.common.nothingHere")} backLabel={t("directory.common.goBack")} message={errorText(error) ?? ""} />;
  if (!company_id) return <EmptyState heading={t("directory.common.nothingHere")} backLabel={t("directory.common.goBack")} message={t("directory.customers.noCompany")} />;

  // True empty dataset
  if (!customers.length) {
    return (
      <EmptyState heading={t("directory.common.nothingHere")} backLabel={t("directory.common.goBack")}
        message={t("directory.customers.empty")}
        showNewButton
        newButtonText={t("directory.customers.newWithPlus")}
        newButtonPath={companyRoute(company_id, "/customers/new")}
      />
    );
  }

  return (
    <div
      className={`min-h-screen bg-gray-50 flex flex-col items-center pt-6 ${A.body}`}
    >
      {/* Header (locked: px-2 + mb-2) */}
      <div
        className={[
          "sticky top-0 z-30 bg-gray-50 w-full",
          PAGE_W,
          "flex items-center border-b border-gray-100 px-2 pb-2 mb-2",
        ].join(" ")}
      >
        <button
          type="button"
          onClick={() => navigate(-1)}
          className={iconBtnCls}
          aria-label={t("directory.common.back")}
        >
          <ArrowLeftIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>

        <h2 className={`flex-1 text-center tracking-tight ${A.title}`}>
          {t("directory.customers.title")}
        </h2>

        <button
          type="button"
          onClick={() => navigate(companyRoute(company_id, "/"))}
          className={iconBtnCls}
          aria-label={t("directory.common.close")}
        >
          <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>
      </div>

      {/* Shared ListToolbar (no date range) */}
      <ListToolbar moreLabel={t("directory.common.more")} sortLabel={t("directory.common.sort")}
        maxWidthClass={PAGE_W}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={t("directory.customers.search")}
        sortValue={sortValue}
        sortOptions={sortOptions}
        onSortChange={onToolbarSortChange}
        showDateRange={false}
        newItems={newItems}
        moreItems={moreItems}
        rightSummary={
          <div className="text-right leading-tight">
            <div className={A.label}>{t("directory.customers.title")}</div>
            <div className={`text-[13px] font-semibold text-gray-900 ${A.num}`}>
              {totalFiltered}
            </div>
          </div>
        }
      />

      {/* Filter produced no results */}
      {customers.length > 0 && totalFiltered === 0 ? (
        <div className={`w-full ${PAGE_W} px-2`}>
          <div className={`${A.card} p-6 text-center`}>
            <div className="text-[13px] text-gray-600">{t("directory.common.noResults")}</div>
            <button
              type="button"
              onClick={clearFilters}
              className={`ml-2 ${linkCls}`}
            >
              {t("directory.common.clearFilters")}
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className={`w-full ${PAGE_W} px-2 grid gap-3 md:hidden`}>
            {pagedCustomers.map((c) => (
              <div
                key={c.id}
                className={`${A.card} p-4 ${cardHoverCls}`}
                onClick={() =>
                  navigate(companyRoute(company_id, `/customers/${c.id}`))
                }
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter")
                    navigate(companyRoute(company_id, `/customers/${c.id}`));
                }}
                aria-label={t("directory.customers.view", { name: c.name })}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {c.name || "—"}
                    </div>
                    <div className={`mt-1 ${A.label} truncate`}>
                      {c.email || "—"}
                    </div>
                    <div className={`${A.label} truncate`}>
                      {c.mobile || "—"}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination (mobile) — ✅ tertiary button fix */}
          <div
            className={`md:hidden flex justify-between items-center mt-4 w-full ${PAGE_W} px-2`}
          >
            <span className={`${A.label} text-gray-500 select-none`}>
              {t("directory.common.showing", { from: fromRow, to: toRow, total: totalFiltered })}
            </span>
            <div className="flex gap-2">
              <Button
                variant="tertiary"
                minWidth="0px"
                className={pagerBtnExtraCls}
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                type="button"
              >
                {t("directory.common.prev")}
              </Button>
              <Button
                variant="tertiary"
                minWidth="0px"
                className={pagerBtnExtraCls}
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                type="button"
              >
                {t("directory.common.next")}
              </Button>
            </div>
          </div>

          {/* Desktop Table */}
          <div className={`hidden md:block w-full ${PAGE_W} mx-auto px-2 mt-3`}>
            <ListTableShell>
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b">
                    <th className="py-2 px-4 text-left text-xs text-gray-500">
                      <button
                        type="button"
                        onClick={() => onHeaderSort("name")}
                        className="inline-flex items-center gap-1 hover:text-gray-700"
                        title={t("directory.common.sortName")}
                      >
                        {t("directory.common.name")} {sortArrow("name")}
                      </button>
                    </th>

                    <th className="py-2 px-2 text-left text-xs text-gray-500">
                      <button
                        type="button"
                        onClick={() => onHeaderSort("email")}
                        className="inline-flex items-center gap-1 hover:text-gray-700"
                        title={t("directory.common.sortEmail")}
                      >
                        {t("directory.common.email")} {sortArrow("email")}
                      </button>
                    </th>

                    <th className="py-2 px-2 text-left text-xs text-gray-500">
                      <button
                        type="button"
                        onClick={() => onHeaderSort("mobile")}
                        className="inline-flex items-center gap-1 hover:text-gray-700"
                        title={t("directory.common.sortMobile")}
                      >
                        {t("directory.common.mobile")} {sortArrow("mobile")}
                      </button>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {pagedCustomers.map((c) => (
                    <tr
                      key={c.id}
                      className="border-b hover:bg-gray-50 cursor-pointer transition text-gray-700"
                      onClick={() =>
                        navigate(companyRoute(company_id, `/customers/${c.id}`))
                      }
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter")
                          navigate(
                            companyRoute(company_id, `/customers/${c.id}`),
                          );
                      }}
                      style={{ outline: "none" }}
                      aria-label={t("directory.customers.view", { name: c.name })}
                    >
                      <td className="py-2 px-4 font-medium text-gray-900 max-w-[18rem] truncate">
                        {c.name || "—"}
                      </td>

                      <td className="py-2 px-2 text-gray-600 max-w-[18rem] truncate">
                        {c.email || "—"}
                      </td>

                      <td className="py-2 px-2 text-gray-600 max-w-[12rem] truncate">
                        {c.mobile || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ListTableShell>

            {/* Pagination (desktop) — ✅ tertiary button fix */}
            <div className="hidden md:flex justify-between items-center mt-4">
              <span className={`${A.label} text-gray-500 select-none`}>
                {t("directory.common.showing", { from: fromRow, to: toRow, total: totalFiltered })}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="tertiary"
                  minWidth="0px"
                  className={pagerBtnExtraCls}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  type="button"
                >
                  {t("directory.common.prev")}
                </Button>
                <Button
                  variant="tertiary"
                  minWidth="0px"
                  className={pagerBtnExtraCls}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  type="button"
                >
                  {t("directory.common.next")}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
